import { useEffect, useRef, useState } from "react";
import { Check, ChevronsUpDown, Search, Building2, Plus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Input } from "./ui/input";
import { cn } from "../lib/utils";
import api from "../lib/api";

// Searchable lookup for existing accounts. Selecting one fills the company name
// and links the account id; typing a new name is kept as free text (new company).
export function AccountLookup({ value, onSelect, testid }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/accounts?limit=8${query ? `&search=${encodeURIComponent(query)}` : ""}`);
        setResults(data.items || []);
      } catch { setResults([]); } finally { setLoading(false); }
    }, 250);
    return () => timer.current && clearTimeout(timer.current);
  }, [query, open]);

  const pick = (name, accountId) => {
    onSelect(name, accountId);
    setOpen(false);
    setQuery("");
  };

  const trimmed = query.trim();
  const exact = results.some((r) => (r.company_name || "").toLowerCase() === trimmed.toLowerCase());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
          data-testid={testid}
        >
          <span className={cn("flex items-center gap-2 truncate", !value && "text-muted-foreground")}>
            <Building2 className="h-4 w-4 shrink-0 opacity-60" />
            {value || "Search or add a company…"}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="flex items-center border-b border-border px-3">
          <Search className="h-4 w-4 shrink-0 opacity-50" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type a company name…"
            className="border-0 focus-visible:ring-0 h-10 px-2"
            data-testid={`${testid}-search`}
          />
        </div>
        <div className="max-h-64 overflow-y-auto slim-scroll py-1">
          {loading && <div className="px-3 py-2 text-sm text-muted-foreground">Searching…</div>}
          {!loading && results.length === 0 && !trimmed && (
            <div className="px-3 py-2 text-sm text-muted-foreground">Start typing to find an account</div>
          )}
          {!loading && results.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => pick(r.company_name, r.id)}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-left hover:bg-accent transition-colors"
              data-testid={`account-option-${r.id}`}
            >
              <Check className={cn("h-4 w-4 shrink-0", value === r.company_name ? "opacity-100 text-primary" : "opacity-0")} />
              <span className="truncate">{r.company_name}</span>
            </button>
          ))}
          {!loading && trimmed && !exact && (
            <button
              type="button"
              onClick={() => pick(trimmed, null)}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-left hover:bg-accent transition-colors border-t border-border"
              data-testid="account-option-create"
            >
              <Plus className="h-4 w-4 shrink-0 text-primary" />
              <span>Use new company “<span className="font-medium">{trimmed}</span>”</span>
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
